/* =====================================================
   DRIPCO — Global JS
   Vanilla JS, no framework. Progressive enhancement.
   ===================================================== */
document.documentElement.classList.remove('no-js');
const PREFERS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function fetchConfig(type = 'json') {
  return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: `application/${type}` } };
}
function debounce(fn, wait) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
function rafThrottle(fn) {
  let queued = false;
  return function throttled(...args) {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      fn.apply(this, args);
    });
  };
}

const ScrollLock = {
  count: 0,
  enable() { if (this.count === 0) document.documentElement.classList.add('scroll-locked'); this.count += 1; },
  disable() { this.count = Math.max(0, this.count - 1); if (this.count === 0) document.documentElement.classList.remove('scroll-locked'); },
};

(function stickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const onScroll = rafThrottle(() => header.classList.toggle('is-scrolled', window.scrollY > 12));
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

(function revealOnScroll() {
  const selector = '.reveal, .section-head, .step';
  const items = document.querySelectorAll(selector);
  if (!items.length) return;
  const animationsOff = PREFERS_REDUCED_MOTION || document.body.dataset.animations === 'false' || !('IntersectionObserver' in window);
  if (animationsOff) { items.forEach((el) => el.classList.add('is-visible')); return; }
  document.querySelectorAll('.product-grid, .category-grid, .brand-grid, .steps-grid').forEach((grid) => {
    Array.from(grid.children).forEach((child, i) => {
      const target = child.matches(selector) ? child : child.querySelector(selector);
      if (target) target.style.setProperty('--reveal-delay', `${Math.min(i, 8) * 65}ms`);
    });
  });
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      io.unobserve(entry.target);
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });
  items.forEach((el) => io.observe(el));
  requestAnimationFrame(() => {
    const cutoff = window.innerHeight * 0.9;
    const inView = [];
    items.forEach((el) => { if (el.getBoundingClientRect().top < cutoff) inView.push(el); });
    inView.forEach((el) => el.classList.add('is-visible'));
  });
})();

(function imageLoadStates() {
  const SKELETON_PARENTS = '.product-card__media, .category-tile, .product__gallery-main, .brand-tile';
  const reveal = (img) => {
    if (img.dataset.imgDone) return;
    img.dataset.imgDone = 'true';
    img.classList.remove('img-loading');
    img.classList.add('img-loaded');
    img.closest(SKELETON_PARENTS)?.removeAttribute('data-img-pending');
  };
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.complete && img.naturalWidth > 0) return;
    const holder = img.closest(SKELETON_PARENTS);
    if (holder) holder.setAttribute('data-img-pending', '');
    if (!PREFERS_REDUCED_MOTION) img.classList.add('img-loading');
    img.addEventListener('load', () => reveal(img), { once: true });
    img.addEventListener('error', () => reveal(img), { once: true });
    if (img.complete) reveal(img);
  });
  window.addEventListener('load', () => setTimeout(() => document.querySelectorAll('img.img-loading').forEach(reveal), 1200));
})();

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
class ThemeDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.drawer__overlay');
    this.panel = this.querySelector('.drawer__panel');
    this.overlay?.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (link && !link.hasAttribute('target') && !link.hasAttribute('data-drawer-close')) this.classList.remove('is-open');
    });
  }
  onKeydown(e) {
    if (!this.classList.contains('is-open')) return;
    if (e.key === 'Escape') { e.stopPropagation(); this.close(); return; }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(this.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  open(opener) {
    if (this.classList.contains('is-open')) return;
    this.opener = opener || document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    ScrollLock.enable();
    requestAnimationFrame(() => (this.querySelector('[data-drawer-autofocus]') || this.querySelector('[data-drawer-close]') || this.querySelector(FOCUSABLE))?.focus({ preventScroll: true }));
  }
  close() {
    if (!this.classList.contains('is-open')) return;
    const focusWasInside = this.contains(document.activeElement);
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    ScrollLock.disable();
    if (focusWasInside) this.opener?.focus?.({ preventScroll: true });
  }
}
customElements.define('theme-drawer', ThemeDrawer);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
  if (drawer && opener.dataset.fullSrc) {
    const img = drawer.querySelector('[data-lightbox-img]');
    if (img) { img.src = opener.dataset.fullSrc; img.alt = opener.dataset.fullAlt || ''; }
  }
  drawer?.open(opener);
});

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-mobile-submenu-toggle]');
  if (!trigger) return;
  const sub = document.getElementById(trigger.getAttribute('aria-controls'));
  if (!sub) return;
  const isOpen = sub.classList.toggle('is-open');
  trigger.setAttribute('aria-expanded', String(isOpen));
});

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.accordion__trigger');
  if (!trigger) return;
  if (!document.getElementById(trigger.getAttribute('aria-controls'))) return;
  trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
});

const replayTimers = new WeakMap();
function replayClass(el, cls, ms) {
  if (!el) return;
  clearTimeout(replayTimers.get(el));
  el.classList.remove(cls);
  requestAnimationFrame(() => {
    el.classList.add(cls);
    replayTimers.set(el, setTimeout(() => el.classList.remove(cls), ms));
  });
}
function showToast(message, { type = 'default', duration = 3600 } = {}) {
  const region = document.getElementById('dripco-toast');
  if (!region) return;
  const el = document.createElement('div');
  el.className = `toast${type === 'error' ? ' toast--error' : ''}`;
  el.setAttribute('role', type === 'error' ? 'alert' : 'status');
  el.textContent = message;
  region.appendChild(el);
  requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-visible')));
  const dismiss = () => { el.classList.add('is-leaving'); el.classList.remove('is-visible'); setTimeout(() => el.remove(), PREFERS_REDUCED_MOTION ? 0 : 260); };
  setTimeout(dismiss, duration);
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-qty-step]');
  if (!btn) return;
  const wrapper = btn.closest('.qty-selector');
  const input = wrapper?.querySelector('input[type="number"]');
  if (!input) return;
  const step = parseInt(btn.getAttribute('data-qty-step'), 10);
  const min = parseInt(input.min || '1', 10);
  input.value = Math.max(min, (parseInt(input.value, 10) || min) + step);
  replayClass(input, 'is-stepped', 420);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

const CartAPI = {
  async getCart() { const res = await fetch('/cart.js'); if (!res.ok) throw new Error('Cart unavailable.'); return res.json(); },
  async add(items) {
    const res = await fetch('/cart/add.js', { ...fetchConfig(), body: JSON.stringify({ items }) });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || 'Could not add this item to your cart.'); }
    return res.json();
  },
  async change(line, quantity) {
    const res = await fetch('/cart/change.js', { ...fetchConfig(), body: JSON.stringify({ line, quantity }) });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || 'Could not update your cart.'); }
    return res.json();
  },
  async renderSections(sectionIds) {
    const params = new URLSearchParams({ sections: sectionIds.join(',') });
    const res = await fetch(`${window.location.pathname}?${params.toString()}`);
    if (!res.ok) throw new Error('Could not refresh cart.');
    return res.json();
  },
};
window.CartAPI = CartAPI;

function bumpCartIcon() {
  document.querySelectorAll('[data-drawer-open="cart-drawer"]').forEach((el) => replayClass(el, 'is-bumping', 760));
  document.querySelectorAll('[data-cart-count]').forEach((el) => replayClass(el, 'is-pulsing', 620));
}
function announce(message) {
  const region = document.getElementById('dripco-cart-status');
  if (!region || !message) return;
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = message; });
}
function findProductImage(form) {
  const scope = form.closest('[data-section-id]') || document;
  const candidates = scope.querySelectorAll('.product__gallery-mobile-slide img, .product__gallery-main img, .product-card__media img');
  for (const img of candidates) {
    const r = img.getBoundingClientRect();
    if (r.width > 24 && r.bottom > 0 && r.top < window.innerHeight) return img;
  }
  return candidates[0] || null;
}
function flyToCart(sourceEl) {
  if (PREFERS_REDUCED_MOTION || !sourceEl || typeof sourceEl.animate !== 'function') return;
  const target = document.querySelector('[data-drawer-open="cart-drawer"]');
  if (!target) return;
  const from = sourceEl.getBoundingClientRect(), to = target.getBoundingClientRect();
  if (!from.width || !from.height) return;
  const clone = document.createElement('img');
  clone.src = sourceEl.currentSrc || sourceEl.src; clone.alt = ''; clone.className = 'fly-to-cart';
  clone.style.left = `${from.left}px`; clone.style.top = `${from.top}px`; clone.style.width = `${from.width}px`; clone.style.height = `${from.height}px`;
  document.body.appendChild(clone);
  const dx = to.left + to.width / 2 - (from.left + from.width / 2), dy = to.top + to.height / 2 - (from.top + from.height / 2);
  const done = () => clone.remove();
  const anim = clone.animate([
    { transform: 'translate3d(0,0,0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
    { transform: `translate3d(${dx * 0.42}px, ${dy * 0.3 - 110}px, 0) scale(0.55) rotate(-9deg)`, opacity: 0.95, offset: 0.55 },
    { transform: `translate3d(${dx}px, ${dy}px, 0) scale(0.06) rotate(8deg)`, opacity: 0.1, offset: 1 },
  ], { duration: 780, easing: 'cubic-bezier(0.55, 0, 0.3, 1)', fill: 'forwards' });
  anim.addEventListener('finish', done);
  setTimeout(done, 1600);
}
let lastSubtotalText = null;
function pulseSubtotal() {
  const el = document.querySelector('#cart-drawer [data-cart-subtotal]');
  if (!el) { lastSubtotalText = null; return; }
  const text = el.textContent.trim();
  if (lastSubtotalText !== null && text !== lastSubtotalText) replayClass(el, 'is-updated', 700);
  lastSubtotalText = text;
}
let lastCartCount = null;
function setCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
  if (lastCartCount !== null && count !== lastCartCount) { bumpCartIcon(); announce(`${window.themeStrings?.cartCountLabel || 'Cart items'}: ${count}`); }
  lastCartCount = count;
}

/* Add-to-cart: one authoritative product form submission path. */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-product-form]');
  if (!form) return;
  e.preventDefault();
  const submitBtn = form.querySelector('[type="submit"]');
  const errorEl = form.querySelector('[data-form-error]');
  const formData = new FormData(form);
  const id = formData.get('id');
  const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10));
  submitBtn?.setAttribute('disabled', 'disabled');
  submitBtn?.classList.add('is-loading');
  if (errorEl) errorEl.hidden = true;
  const startedAt = Date.now();
  flyToCart(findProductImage(form));
  try {
    const addedItems = await CartAPI.add([{ id, quantity }]);
    const addedTitle = addedItems?.items?.[0]?.product_title || addedItems?.product_title || '';
    announce(addedTitle ? `${addedTitle} ${window.themeStrings?.addedToCartAnnounce || 'added to cart'}` : window.themeStrings?.addedToCartAnnounce || 'Item added to cart');
    if (submitBtn) {
      const originalHTML = submitBtn.innerHTML;
      submitBtn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="9"/><polyline points="8 12.5 10.7 15 16 9.5"/></svg> ' + (window.themeStrings?.addedToCart || 'Added');
      submitBtn.classList.add('is-added');
      setTimeout(() => { submitBtn.innerHTML = originalHTML; submitBtn.classList.remove('is-added'); }, 1100);
    }
    await refreshCartDrawer();
    if ((document.body.dataset.cartType || 'drawer') === 'drawer') {
      const elapsed = Date.now() - startedAt;
      if (!PREFERS_REDUCED_MOTION && elapsed < 640) await new Promise((resolve) => setTimeout(resolve, 640 - elapsed));
      document.getElementById('cart-drawer')?.open(submitBtn);
    } else {
      window.location.href = '/cart';
    }
  } catch (err) {
    if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
  } finally {
    submitBtn?.removeAttribute('disabled');
    submitBtn?.classList.remove('is-loading');
  }
});

document.addEventListener('change', async (e) => {
  const input = e.target.closest('[data-cart-line-qty]');
  if (!input) return;
  const line = parseInt(input.getAttribute('data-cart-line-qty'), 10);
  const quantity = Math.max(0, parseInt(input.value, 10) || 0);
  const previousValue = input.defaultValue;
  input.disabled = true;
  replayClass(input.closest('.cart-item'), 'is-updating', 500);
  try {
    await CartAPI.change(line, quantity);
    announce(`${window.themeStrings?.quantityUpdatedAnnounce || 'Cart quantity updated to'} ${quantity}`);
    if (document.body.dataset.template === 'cart') window.location.reload();
    else await refreshCartDrawer();
  } catch (err) {
    input.value = previousValue;
    input.disabled = false;
    showToast('Couldn’t update quantity. Please try again.', { type: 'error' });
  }
});

document.addEventListener('click', async (e) => {
  const removeBtn = e.target.closest('[data-cart-line-remove]');
  if (!removeBtn) return;
  e.preventDefault();
  const line = parseInt(removeBtn.getAttribute('data-cart-line-remove'), 10);
  removeBtn.setAttribute('disabled', 'disabled');
  const row = removeBtn.closest('.cart-item');
  if (row) {
    row.style.maxHeight = `${row.scrollHeight}px`;
    requestAnimationFrame(() => row.classList.add('is-removing'));
  }
  const removedTitle = row?.querySelector('.cart-item__title')?.textContent?.trim();
  await new Promise((resolve) => setTimeout(resolve, PREFERS_REDUCED_MOTION ? 0 : 300));
  try {
    await CartAPI.change(line, 0);
    announce(removedTitle ? `${removedTitle} ${window.themeStrings?.removedFromCartAnnounce || 'removed from cart'}` : window.themeStrings?.removedFromCartAnnounce || 'Item removed from cart');
    if (document.body.dataset.template === 'cart') window.location.reload();
    else await refreshCartDrawer();
  } catch (err) {
    if (row) { row.classList.remove('is-removing'); row.style.maxHeight = ''; }
    removeBtn.removeAttribute('disabled');
    showToast('Couldn’t remove that item. Please try again.', { type: 'error' });
  }
});

document.addEventListener('click', (e) => {
  const checkoutBtn = e.target.closest('.btn-checkout');
  if (!checkoutBtn || checkoutBtn.classList.contains('is-loading')) return;
  checkoutBtn.classList.add('is-loading');
});
window.addEventListener('pageshow', (e) => {
  if (e.persisted) document.querySelectorAll('.btn-checkout.is-loading').forEach((btn) => btn.classList.remove('is-loading'));
});

async function refreshCartDrawer() {
  const wrapper = document.getElementById('shopify-section-cart-drawer');
  if (!wrapper) return;
  try {
    const currentDrawer = wrapper.querySelector('theme-drawer');
    const wasOpen = currentDrawer?.classList.contains('is-open');
    const opener = currentDrawer?.opener || document.activeElement;
    const sectionsHtml = await CartAPI.renderSections(['cart-drawer']);
    const html = sectionsHtml['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById('shopify-section-cart-drawer');
    if (!fresh) return;
    wrapper.replaceChildren(...fresh.childNodes);
    const freshDrawer = wrapper.querySelector('theme-drawer');
    if (freshDrawer) {
      freshDrawer.opener = opener;
      if (wasOpen) { freshDrawer.classList.add('is-open'); freshDrawer.setAttribute('aria-hidden', 'false'); }
      const count = parseInt(freshDrawer.dataset.cartItemCount || '', 10);
      if (Number.isFinite(count)) setCartCount(count);
    }
    pulseSubtotal();
  } catch (err) { console.error(err); }
}

window.Shopify = window.Shopify || {};
if (!window.Shopify.formatMoney) window.Shopify.formatMoney = function formatMoney(cents, format) { let value = cents; if (typeof value === 'string') value = value.replace('.', ''); const amount = (value / 100).toFixed(2); return (format || '${{amount}}').replace('{{amount}}', amount).replace('{{ amount }}', amount); };

/* Product variant state only. Gallery media is owned exclusively by product-mobile-performance.js. */
class VariantSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    const dataEl = document.getElementById(`ProductJson-${this.dataset.section}`);
    if (!dataEl) return;
    this.dataset.bound = 'true';
    try { this.variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.variantsByKey = new Map(this.variants.map((v) => [v.options.join('|'), v]));
    this.addEventListener('change', (event) => this.onChange(event));
    this.updateFromForm();
  }
  getSelectedOptions() { return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((el) => el.value); }
  findVariant(options) { return this.variantsByKey.get(options.join('|')); }
  onChange() {
    const options = this.getSelectedOptions();
    const variant = this.findVariant(options);
    this.updateOptionLabels(options);
    this.updateAvailability(options);
    if (!variant) { this.setUnavailable(); this.dispatchEvent(new CustomEvent('dripco:variant-change', { detail: { variant: null } })); return; }
    this.updateURL(variant);
    this.updateVariantInput(variant);
    this.updatePrice(variant);
    this.updateButton(variant);
    this.dispatchEvent(new CustomEvent('dripco:variant-change', { detail: { variant } }));
  }
  updateFromForm() { this.onChange(); }
  updateOptionLabels(options) { this.querySelectorAll('[data-option-index]').forEach((label) => { const index = parseInt(label.dataset.optionIndex, 10); if (options[index]) label.textContent = options[index]; }); }
  updateAvailability(selectedOptions) {
    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const position = parseInt(group.dataset.optionPosition, 10);
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const testOptions = [...selectedOptions]; testOptions[position] = input.value;
        const match = this.findVariant(testOptions); input.disabled = !match || !match.available;
      });
    });
  }
  updateURL(variant) { if (!this.dataset.updateUrl) return; const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url); }
  updateVariantInput(variant) { const input = document.querySelector(`#product-form-${this.dataset.section}`)?.querySelector('input[name="id"]'); if (input) input.value = variant.id; }
  updatePrice(variant) {
    const money = (cents) => window.Shopify.formatMoney(cents, window.themeMoneyFormat);
    const priceWrap = document.getElementById(`price-${this.dataset.section}`);
    if (priceWrap) { let html = `<span class="price--sale">${money(variant.price)}</span>`; if (variant.compare_at_price && variant.compare_at_price > variant.price) html += `<span class="price--compare">${money(variant.compare_at_price)}</span>`; priceWrap.innerHTML = html; }
    const stickyPrice = document.querySelector(`[data-sticky-atc] [data-sticky-atc-price]`); if (stickyPrice) stickyPrice.textContent = money(variant.price);
    const panel = document.getElementById(`preorder-panel-${this.dataset.section}`);
    if (panel) { const pct = parseFloat(panel.dataset.depositPercentage || '50'); const deposit = Math.round((variant.price * pct) / 100); const remaining = variant.price - deposit; panel.querySelector('[data-preorder-deposit]')?.replaceChildren(document.createTextNode(money(deposit))); panel.querySelector('[data-preorder-remaining]')?.replaceChildren(document.createTextNode(money(remaining))); }
  }
  updateButton(variant) {
    const buttons = [document.getElementById(`add-to-cart-${this.dataset.section}`), document.querySelector('[data-sticky-atc] button[type="submit"]')].filter(Boolean);
    buttons.forEach((btn) => { if (!variant.available) { btn.textContent = window.themeStrings?.soldOut || 'Sold Out'; btn.setAttribute('disabled', 'disabled'); } else { btn.textContent = this.dataset.preorder === 'true' ? window.themeStrings?.preorder || 'Pre-Order Now' : window.themeStrings?.addToCart || 'Add to Cart'; btn.removeAttribute('disabled'); } });
  }
  setUnavailable() { const buttons = [document.getElementById(`add-to-cart-${this.dataset.section}`), document.querySelector('[data-sticky-atc] button[type="submit"]')].filter(Boolean); buttons.forEach((btn) => { btn.textContent = window.themeStrings?.unavailable || 'Unavailable'; btn.setAttribute('disabled', 'disabled'); }); }
}
customElements.define('variant-selector', VariantSelector);

document.addEventListener('click', (e) => {
  const thumb = e.target.closest('[data-thumb-src]');
  if (!thumb) return;
  const gallery = thumb.closest('[data-gallery]');
  const main = gallery?.querySelector('[data-gallery-main] img');
  if (main) {
    const src = thumb.dataset.thumbSrc;
    main.style.opacity = '0';
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => { main.src = src; main.style.opacity = '1'; };
    image.onerror = () => { main.style.opacity = '1'; };
    image.src = src;
  }
  gallery?.querySelectorAll('.product__thumb').forEach((t) => t.classList.remove('is-active'));
  thumb.classList.add('is-active');
});

document.addEventListener('click', (e) => {
  const arrow = e.target.closest('[data-gallery-prev], [data-gallery-next]');
  if (!arrow) return;
  const gallery = arrow.closest('[data-gallery]');
  const thumbs = Array.from(gallery?.querySelectorAll('.product__thumb') || []);
  if (!thumbs.length) return;
  const currentIndex = thumbs.findIndex((t) => t.classList.contains('is-active'));
  const delta = arrow.hasAttribute('data-gallery-next') ? 1 : -1;
  const nextIndex = (currentIndex + delta + thumbs.length) % thumbs.length;
  thumbs[nextIndex].click();
});

(function stickyAddToCart() {
  const bar = document.querySelector('[data-sticky-atc]');
  const buyRow = document.querySelector('[data-buy-row]');
  if (!bar || !buyRow || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => entries.forEach((entry) => { bar.hidden = entry.isIntersecting; }), { threshold: 0, rootMargin: '0px 0px -20% 0px' });
  io.observe(buyRow);
})();

(function heroSound() {
  const toggle = document.querySelector('[data-hero-sound-toggle]'), audio = document.querySelector('[data-hero-audio]');
  if (!toggle || !audio) return;
  toggle.addEventListener('click', () => { const enabling = audio.muted; audio.muted = !enabling; if (enabling) audio.play().catch(() => {}); toggle.setAttribute('aria-pressed', String(enabling)); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) audio.pause(); else if (!audio.muted) audio.play().catch(() => {}); });
})();

class PredictiveSearch extends HTMLElement {
  connectedCallback() { this.input = this.querySelector('input[type="search"]'); this.panel = this.querySelector('.predictive-search__panel'); if (!this.input || !this.panel) return; this.panel.setAttribute('role', 'listbox'); this.activeIndex = -1; this.input.addEventListener('input', debounce(() => this.onInput(), 220)); this.input.addEventListener('keydown', (e) => this.onKeydown(e)); document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); }); }
  getOptions() { return Array.from(this.panel.querySelectorAll('[role="option"]')); }
  onKeydown(e) { if (e.key === 'Escape') { this.close(); return; } if (this.panel.hidden) return; const options = this.getOptions(); if (!options.length) return; if (e.key === 'ArrowDown') { e.preventDefault(); this.setActive((this.activeIndex + 1) % options.length, options); } else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActive((this.activeIndex - 1 + options.length) % options.length, options); } else if (e.key === 'Enter' && this.activeIndex > -1) { e.preventDefault(); options[this.activeIndex]?.click(); } }
  setActive(index, options) { options.forEach((opt) => { opt.classList.remove('is-active'); opt.setAttribute('aria-selected', 'false'); }); this.activeIndex = index; const active = options[index]; if (!active) { this.input.removeAttribute('aria-activedescendant'); return; } active.classList.add('is-active'); active.setAttribute('aria-selected', 'true'); active.scrollIntoView({ block: 'nearest' }); this.input.setAttribute('aria-activedescendant', active.id); }
  close() { this.panel.hidden = true; this.activeIndex = -1; this.input.removeAttribute('aria-activedescendant'); }
  async onInput() { const q = this.input.value.trim(); if (q.length < 2) { this.close(); return; } try { const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last`); const data = await res.json(); this.render(data.resources.results, q); } catch (err) { this.close(); } }
  render(results, q) {
    const { products = [], collections = [], pages = [] } = results; const total = products.length + collections.length + pages.length; const uid = `ps-${Date.now()}`; let optionIndex = 0;
    const escape = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[char]));
    if (!total) { this.panel.innerHTML = `<p class="body-sm text-muted" style="padding:16px;">${escape(window.themeStrings?.noResults || 'No results for')} &ldquo;${escape(q)}&rdquo;</p>`; this.panel.hidden = false; this.activeIndex = -1; return; }
    let html = '';
    const addItem = (item, withImage) => { const url = escape(item.url); const title = escape(item.title); const price = escape(item.price || ''); const image = withImage && item.image ? `<img src="${escape(item.image)}" alt="" width="46" height="58" loading="lazy">` : ''; html += `<a class="predictive-search__item" role="option" id="${uid}-${optionIndex}" href="${url}">${image}<span><span class="predictive-search__item-title">${title}</span>${price ? `<br><span class="predictive-search__item-price">${price}</span>` : ''}</span></a>`; optionIndex += 1; };
    if (products.length) { html += `<div class="predictive-search__group-title">${escape(window.themeStrings?.products || 'Products')}</div>`; products.forEach((item) => addItem(item, true)); }
    if (collections.length) { html += `<div class="predictive-search__group-title">${escape(window.themeStrings?.collections || 'Collections')}</div>`; collections.forEach((item) => addItem(item, false)); }
    if (pages.length) { html += `<div class="predictive-search__group-title">${escape(window.themeStrings?.pages || 'Pages')}</div>`; pages.forEach((item) => addItem(item, false)); }
    this.panel.innerHTML = html; this.panel.hidden = false; this.activeIndex = -1; this.input.removeAttribute('aria-activedescendant');
  }
}
customElements.define('predictive-search', PredictiveSearch);

class SendPhotoForm extends HTMLElement {
  connectedCallback() { this.fileInput = this.querySelector('input[type="file"]'); this.preview = this.querySelector('[data-preview]'); this.removeBtn = this.querySelector('[data-remove-image]'); this.label = this.querySelector('[data-dropzone-label]'); this.fileInput?.addEventListener('change', () => this.onFile()); this.removeBtn?.addEventListener('click', () => this.onRemove()); }
  onFile() { const file = this.fileInput.files[0]; if (!file) return; if (this.objectUrl) URL.revokeObjectURL(this.objectUrl); this.objectUrl = URL.createObjectURL(file); if (this.preview) { this.preview.src = this.objectUrl; this.preview.hidden = false; } if (this.removeBtn) this.removeBtn.hidden = false; if (this.label) this.label.textContent = file.name; }
  onRemove() { if (this.objectUrl) { URL.revokeObjectURL(this.objectUrl); this.objectUrl = null; } this.fileInput.value = ''; if (this.preview) this.preview.hidden = true; if (this.removeBtn) this.removeBtn.hidden = true; if (this.label) this.label.textContent = this.getAttribute('data-attach-label') || 'Attach a photo'; }
}
customElements.define('send-photo-form', SendPhotoForm);

(function prefetchOnIntent() {
  const conn = navigator.connection; if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return; if (!window.matchMedia('(hover: hover)').matches) return;
  const prefetched = new Set();
  const prefetch = (url) => { if (prefetched.has(url) || prefetched.size > 12) return; prefetched.add(url); const link = document.createElement('link'); link.rel = 'prefetch'; link.href = url; document.head.appendChild(link); };
  document.addEventListener('mouseover', (e) => { const link = e.target.closest('a[href]'); if (!link || link.target || link.hasAttribute('download')) return; const url = new URL(link.href, window.location.origin); if (url.origin !== window.location.origin || url.pathname === window.location.pathname || /\/(cart|account|checkout)/.test(url.pathname)) return; prefetch(url.href); }, { passive: true, capture: true });
})();

(function megaMenu() {
  const items = document.querySelectorAll('[data-mega-menu-item]'); if (!items.length) return;
  const closeItem = (item) => { const trigger = item.querySelector('[data-mega-menu-trigger]'); if (!item.classList.contains('is-open')) return; item.classList.remove('is-open'); trigger?.setAttribute('aria-expanded', 'false'); };
  const closeAll = (except) => items.forEach((item) => { if (item !== except) closeItem(item); });
  items.forEach((item) => { const trigger = item.querySelector('[data-mega-menu-trigger]'); if (!trigger) return; trigger.addEventListener('click', (e) => { const isOpen = item.classList.contains('is-open'); e.preventDefault(); closeAll(item); if (isOpen) closeItem(item); else { item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); } }); });
  document.addEventListener('keydown', (e) => { if (e.key !== 'Escape') return; const openItem = document.querySelector('[data-mega-menu-item].is-open'); if (!openItem) return; closeItem(openItem); openItem.querySelector('[data-mega-menu-trigger]')?.focus(); });
  document.addEventListener('click', (e) => { const openItem = document.querySelector('[data-mega-menu-item].is-open'); if (openItem && !openItem.contains(e.target)) closeItem(openItem); });
  document.addEventListener('click', (e) => { if (e.target.closest('.mega-menu__link')) { const openItem = e.target.closest('[data-mega-menu-item]'); if (openItem) closeItem(openItem); } });
})();

const Wishlist = {
  key: 'dripco:wishlist', _cache: null,
  _read() { if (this._cache) return this._cache; try { const raw = window.localStorage.getItem(this.key); this._cache = raw ? JSON.parse(raw) : []; if (!Array.isArray(this._cache)) this._cache = []; } catch (err) { this._cache = []; } return this._cache; },
  _write(list) { this._cache = list; try { window.localStorage.setItem(this.key, JSON.stringify(list)); } catch (err) {} document.dispatchEvent(new CustomEvent('dripco:wishlist:change', { detail: { handles: list.slice() } })); },
  getAll() { return this._read().slice(); }, count() { return this._read().length; }, has(handle) { return this._read().includes(handle); }, add(handle) { if (!handle || this.has(handle)) return; this._write([...this._read(), handle]); }, remove(handle) { const list = this._read().filter((h) => h !== handle); if (list.length !== this._read().length) this._write(list); }, toggle(handle) { const nowActive = !this.has(handle); if (nowActive) this.add(handle); else this.remove(handle); return nowActive; },
};
window.Wishlist = Wishlist;
function syncWishlistUI() { const count = Wishlist.count(); document.querySelectorAll('[data-wishlist-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; }); document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => { const id = btn.getAttribute('data-wishlist-toggle'); setHeartState(btn, Wishlist.has(id), false); }); }
function setHeartState(btn, active, animate) { btn.classList.toggle('is-active', active); btn.setAttribute('aria-pressed', String(active)); const outline = btn.querySelector('[data-heart-outline]'), filled = btn.querySelector('[data-heart-filled]'); if (outline) outline.hidden = active; if (filled) filled.hidden = !active; const label = active ? btn.dataset.labelRemove : btn.dataset.labelAdd; if (label) btn.setAttribute('aria-label', label); if (animate) replayClass(btn, 'is-toggling', 400); }
document.addEventListener('click', (e) => { const btn = e.target.closest('[data-wishlist-toggle]'); if (!btn) return; e.preventDefault(); e.stopPropagation(); const card = btn.closest('[data-product-card]'); const handle = btn.dataset.wishlistHandle || card?.dataset.productHandle || btn.dataset.wishlistToggle; const active = Wishlist.toggle(handle); setHeartState(btn, active, true); });
document.addEventListener('dripco:wishlist:change', syncWishlistUI); document.addEventListener('DOMContentLoaded', syncWishlistUI); syncWishlistUI();

function findQuickAddVariant(variants, options) { return variants.find((variant) => variant.options.every((opt, i) => opt === options[i])); }
document.addEventListener('click', (e) => { const trigger = e.target.closest('[data-quick-add-trigger]'); if (!trigger) return; e.preventDefault(); const wrap = trigger.closest('[data-quick-add]'); if (!wrap) return; if (trigger.dataset.singleVariant === 'true') { quickAddToCart(trigger.dataset.variantId, trigger, wrap); return; } const popover = wrap.querySelector('[data-quick-add-popover]'); if (!popover) return; const opening = popover.hidden; document.querySelectorAll('[data-quick-add-popover]').forEach((p) => { p.hidden = true; }); document.querySelectorAll('[data-quick-add-trigger][aria-expanded="true"]').forEach((t) => t.setAttribute('aria-expanded', 'false')); popover.hidden = !opening; trigger.setAttribute('aria-expanded', String(opening)); });
document.addEventListener('click', (e) => { if (e.target.closest('[data-quick-add]')) return; document.querySelectorAll('[data-quick-add-popover]:not([hidden])').forEach((p) => { p.hidden = true; p.closest('[data-quick-add]')?.querySelector('[data-quick-add-trigger]')?.setAttribute('aria-expanded', 'false'); }); });
document.addEventListener('click', async (e) => { const confirmBtn = e.target.closest('[data-quick-add-confirm]'); if (!confirmBtn) return; e.preventDefault(); const popover = confirmBtn.closest('[data-quick-add-popover]'); const wrap = confirmBtn.closest('[data-quick-add]'); const messageEl = popover?.querySelector('[data-quick-add-message]'); const variantsEl = popover?.querySelector('[data-quick-add-variants]'); if (!popover || !wrap || !variantsEl) return; let variants = []; try { variants = JSON.parse(variantsEl.textContent); } catch (err) { return; } const optionGroups = Array.from(popover.querySelectorAll('[data-quick-add-option]')).sort((a, b) => parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10)); const selected = optionGroups.map((group) => group.querySelector('[data-quick-add-option-input]:checked')?.value); if (selected.some((v) => !v)) { if (messageEl) { messageEl.textContent = window.themeStrings?.quickAddChooseOptions || 'Please select options.'; messageEl.hidden = false; messageEl.classList.add('quick-add__message--error'); } return; } const variant = findQuickAddVariant(variants, selected); if (!variant || !variant.available) { if (messageEl) { messageEl.textContent = window.themeStrings?.unavailable || 'Unavailable'; messageEl.hidden = false; messageEl.classList.add('quick-add__message--error'); } return; } if (messageEl) messageEl.hidden = true; await quickAddToCart(variant.id, confirmBtn, wrap); popover.hidden = true; wrap.querySelector('[data-quick-add-trigger]')?.setAttribute('aria-expanded', 'false'); });
async function quickAddToCart(variantId, triggerBtn, wrap) { if (!variantId) return; triggerBtn.classList.add('is-loading'); triggerBtn.setAttribute('disabled', 'disabled'); const sourceImg = wrap.closest('[data-product-card]')?.querySelector('.product-card__img--front'); try { const added = await CartAPI.add([{ id: variantId, quantity: 1 }]); if (sourceImg) flyToCart(sourceImg); const title = added?.items?.[0]?.product_title; showToast(title ? `${title} ${window.themeStrings?.addedToCartAnnounce || 'added to cart'}` : window.themeStrings?.addedToCart || 'Added'); announce(title ? `${title} ${window.themeStrings?.addedToCartAnnounce || 'added to cart'}` : window.themeStrings?.addedToCartAnnounce || 'Item added to cart'); await refreshCartDrawer(); if ((document.body.dataset.cartType || 'drawer') === 'drawer') document.getElementById('cart-drawer')?.open(triggerBtn); } catch (err) { showToast(err.message || 'Could not add this item to your cart.', { type: 'error' }); } finally { triggerBtn.classList.remove('is-loading'); triggerBtn.removeAttribute('disabled'); } }

/* Mobile toolbar: fixed bottom navigation with a single active indicator. */
(function mobileToolbar() {
  const toolbar = document.querySelector('[data-mobile-toolbar]');
  if (!toolbar) return;
  const items = Array.from(toolbar.querySelectorAll('[data-toolbar-item]'));
  if (!items.length) return;
  let activeIndex = -1;
  const setActiveIndex = (index) => { toolbar.style.setProperty('--toolbar-active', index); items.forEach((item, i) => item.classList.toggle('is-active', i === index)); };
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  items.forEach((item, i) => { const match = item.dataset.toolbarMatch; if (!match) return; if (match === '^/$') { if (path === '') activeIndex = i; } else if (path.includes(match)) activeIndex = i; });
  if (activeIndex > -1) setActiveIndex(activeIndex);
  items.forEach((item, i) => { if (item.tagName !== 'BUTTON') return; item.addEventListener('click', () => { const previous = activeIndex; setActiveIndex(i); setTimeout(() => setActiveIndex(previous > -1 ? previous : i), 900); }); });
})();

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    this.dots = Array.from(this.querySelectorAll('.announcement-bar__dot'));
    this.dismissBtn = this.querySelector('[data-announcement-dismiss]');
    this.storageKey = `dripco:announcement-dismissed:${this.dataset.sectionId || 'default'}`;
    this.index = 0;
    if (this.dataset.dismissible === 'true') {
      let dismissed = false;
      try { dismissed = window.sessionStorage.getItem(this.storageKey) === 'true'; } catch (err) { dismissed = false; }
      if (dismissed) { this.hidden = true; return; }
      this.hidden = false;
    }
    this.dismissBtn?.addEventListener('click', () => { this.hidden = true; try { window.sessionStorage.setItem(this.storageKey, 'true'); } catch (err) {} });
    if (this.slides.length > 1 && !PREFERS_REDUCED_MOTION) {
      const ms = Math.max(3000, parseInt(this.dataset.rotateMs, 10) || 5000);
      this.timer = setInterval(() => this.next(), ms);
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume(ms));
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume(ms));
      this.onVisibility = () => (document.hidden ? this.pause() : this.resume(ms));
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }
  pause() { clearInterval(this.timer); }
  resume(ms) { clearInterval(this.timer); if (document.hidden) return; if (this.slides.length > 1 && !PREFERS_REDUCED_MOTION) this.timer = setInterval(() => this.next(), ms); }
  next() { if (!this.slides.length) return; this.slides[this.index]?.classList.remove('is-active'); this.dots[this.index]?.classList.remove('is-active'); this.index = (this.index + 1) % this.slides.length; this.slides[this.index]?.classList.add('is-active'); this.dots[this.index]?.classList.add('is-active'); }
  disconnectedCallback() { clearInterval(this.timer); if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility); }
}
if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

(function confirmModal() {
  let pendingForm = null;
  document.addEventListener('click', (e) => { const trigger = e.target.closest('[data-confirm-open]'); if (!trigger) return; const modal = document.getElementById(trigger.getAttribute('data-confirm-open')); pendingForm = document.getElementById(trigger.getAttribute('data-confirm-form')); modal?.open(trigger); });
  document.addEventListener('click', (e) => { const submitBtn = e.target.closest('[data-confirm-submit]'); if (!submitBtn || !pendingForm) return; const form = pendingForm; pendingForm = null; form.requestSubmit ? form.requestSubmit() : form.submit(); });
})();

(function passwordValidation() {
  const forms = document.querySelectorAll('[data-password-form]');
  if (!forms.length) return;
  const score = (value) => { if (!value) return 0; let s = 0; if (value.length >= 8) s++; if (value.length >= 12) s++; if (/[a-z]/.test(value) && /[A-Z]/.test(value)) s++; if (/[0-9]/.test(value)) s++; if (/[^A-Za-z0-9]/.test(value)) s++; return s <= 1 ? 1 : s <= 3 ? 2 : 3; };
  const labels = { 1: 'Weak', 2: 'Medium', 3: 'Strong' }, keys = { 1: 'weak', 2: 'medium', 3: 'strong' };
  forms.forEach((form) => {
    const pass = form.querySelector('[data-password-input]'), confirm = form.querySelector('[data-password-confirm]'), wrap = form.querySelector('[data-password-strength]'), label = form.querySelector('[data-password-strength-label]'), match = form.querySelector('[data-password-match]');
    const checkMatch = () => { if (!confirm || !match) return; if (!confirm.value) { match.hidden = true; match.removeAttribute('data-state'); return; } const ok = confirm.value === (pass?.value || ''); match.hidden = false; match.dataset.state = ok ? 'match' : 'mismatch'; match.textContent = ok ? (window.themeStrings?.passwordMatch || 'Passwords match.') : (window.themeStrings?.passwordMismatch || 'Passwords do not match.'); };
    pass?.addEventListener('input', () => { const value = pass.value; if (!wrap) return; if (!value) { wrap.hidden = true; wrap.removeAttribute('data-strength'); return; } const level = score(value); wrap.hidden = false; wrap.dataset.strength = keys[level]; if (label) label.textContent = window.themeStrings?.[`passwordStrength${keys[level]}`] || labels[level]; if (confirm?.value) checkMatch(); });
    confirm?.addEventListener('input', checkMatch);
  });
})();

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!form.matches('#CollectionFilterForm, #SearchFilterForm, .search-form')) return;
  let bar = document.getElementById('dripco-nav-progress');
  if (!bar) { bar = document.createElement('div'); bar.id = 'dripco-nav-progress'; bar.className = 'nav-progress'; document.body.appendChild(bar); }
  requestAnimationFrame(() => bar.classList.add('is-active'));
  document.querySelectorAll('.filters, .collection-toolbar').forEach((el) => el.classList.add('is-loading'));
  form.querySelectorAll('input, select, button').forEach((el) => { el.disabled = true; });
});
